import React, { useEffect, useState, useCallback, useRef } from "react";

export default function ImageLightbox({
  images,
  currentIndex,
  isOpen,
  onClose,
  onNavigate,
}) {
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const touchStartRef = useRef(null);

  const totalImages = images?.length || 0;
  const currentImage = images?.[currentIndex];

  // Reset zoom and pan whenever current image or modal state changes
  useEffect(() => {
    setScale(1);
    setPan({ x: 0, y: 0 });
    setIsDragging(false);
  }, [currentIndex, isOpen]);

  const handlePrev = useCallback(() => {
    if (totalImages <= 1) return;
    setScale(1);
    setPan({ x: 0, y: 0 });
    onNavigate((currentIndex - 1 + totalImages) % totalImages);
  }, [currentIndex, totalImages, onNavigate]);

  const handleNext = useCallback(() => {
    if (totalImages <= 1) return;
    setScale(1);
    setPan({ x: 0, y: 0 });
    onNavigate((currentIndex + 1) % totalImages);
  }, [currentIndex, totalImages, onNavigate]);

  const handleZoomIn = (e) => {
    e && e.stopPropagation();
    setScale((prev) => Math.min(Number((prev + 0.5).toFixed(1)), 3.5));
  };

  const handleZoomOut = (e) => {
    e && e.stopPropagation();
    setScale((prev) => {
      const next = Math.max(Number((prev - 0.5).toFixed(1)), 1);
      if (next === 1) setPan({ x: 0, y: 0 });
      return next;
    });
  };

  const handleResetZoom = (e) => {
    e && e.stopPropagation();
    setScale(1);
    setPan({ x: 0, y: 0 });
  };

  const handleToggleZoom = (e) => {
    e.stopPropagation();
    if (scale === 1) {
      setScale(2);
    } else {
      handleResetZoom();
    }
  };

  // Keyboard navigation & zoom shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (scale > 1) {
          handleResetZoom();
        } else {
          onClose();
        }
      } else if (e.key === "ArrowLeft") {
        if (scale === 1) handlePrev();
      } else if (e.key === "ArrowRight") {
        if (scale === 1) handleNext();
      } else if (e.key === "+" || e.key === "=") {
        handleZoomIn();
      } else if (e.key === "-") {
        handleZoomOut();
      } else if (e.key === "0") {
        handleResetZoom();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, onClose, handlePrev, handleNext, scale]);

  // Mouse wheel zoom
  const handleWheel = (e) => {
    e.stopPropagation();
    if (e.deltaY < 0) {
      setScale((prev) => Math.min(Number((prev + 0.25).toFixed(2)), 3.5));
    } else if (e.deltaY > 0) {
      setScale((prev) => {
        const next = Math.max(Number((prev - 0.25).toFixed(2)), 1);
        if (next === 1) setPan({ x: 0, y: 0 });
        return next;
      });
    }
  };

  // Mouse drag handlers for panning
  const handleMouseDown = (e) => {
    if (scale <= 1) return;
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX - pan.x,
      y: e.clientY - pan.y,
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging || scale <= 1) return;
    setPan({
      x: e.clientX - dragStartRef.current.x,
      y: e.clientY - dragStartRef.current.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch handlers for mobile swipe / pan
  const handleTouchStart = (e) => {
    if (scale > 1) {
      const touch = e.touches[0];
      touchStartRef.current = {
        panStartX: touch.clientX - pan.x,
        panStartY: touch.clientY - pan.y,
      };
    } else {
      touchStartRef.current = { clientX: e.touches[0].clientX };
    }
  };

  const handleTouchMove = (e) => {
    if (scale > 1 && touchStartRef.current?.panStartX !== undefined) {
      const touch = e.touches[0];
      setPan({
        x: touch.clientX - touchStartRef.current.panStartX,
        y: touch.clientY - touchStartRef.current.panStartY,
      });
    }
  };

  const handleTouchEnd = (e) => {
    if (scale === 1 && touchStartRef.current?.clientX !== undefined) {
      const touchEndX = e.changedTouches[0].clientX;
      const diff = touchStartRef.current.clientX - touchEndX;

      if (diff > 50) {
        handleNext();
      } else if (diff < -50) {
        handlePrev();
      }
    }
    touchStartRef.current = null;
  };

  if (!isOpen || !currentImage) {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Image gallery lightbox"
      onClick={scale > 1 ? handleResetZoom : onClose}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(10, 10, 25, 0.96)",
        zIndex: 99999,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px",
        boxSizing: "border-box",
        backdropFilter: "blur(8px)",
        userSelect: "none",
      }}
    >
      {/* Top Bar: Counter, Zoom Controls, and Close Button */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "1200px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 12px",
          zIndex: 100002,
        }}
      >
        {/* Left: Photo Counter */}
        <span
          style={{
            color: "#ffbc3b",
            fontSize: "15px",
            fontWeight: "700",
            letterSpacing: "1px",
            textTransform: "uppercase",
          }}
        >
          Photo {currentIndex + 1} of {totalImages}
        </span>

        {/* Center: Zoom Controls */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            backgroundColor: "rgba(255, 255, 255, 0.12)",
            padding: "4px 12px",
            borderRadius: "24px",
          }}
        >
          <button
            onClick={handleZoomOut}
            disabled={scale <= 1}
            title="Zoom Out (-)"
            aria-label="Zoom Out"
            style={{
              background: "none",
              border: "none",
              color: scale <= 1 ? "rgba(255,255,255,0.3)" : "#ffffff",
              fontSize: "18px",
              cursor: scale <= 1 ? "not-allowed" : "pointer",
              padding: "0 6px",
              lineHeight: 1,
            }}
          >
            −
          </button>

          <span
            style={{
              color: "#ffffff",
              fontSize: "13px",
              fontWeight: "600",
              minWidth: "46px",
              textAlign: "center",
            }}
          >
            {Math.round(scale * 100)}%
          </span>

          <button
            onClick={handleZoomIn}
            disabled={scale >= 3.5}
            title="Zoom In (+)"
            aria-label="Zoom In"
            style={{
              background: "none",
              border: "none",
              color: scale >= 3.5 ? "rgba(255,255,255,0.3)" : "#ffffff",
              fontSize: "18px",
              cursor: scale >= 3.5 ? "not-allowed" : "pointer",
              padding: "0 6px",
              lineHeight: 1,
            }}
          >
            +
          </button>

          {scale > 1 && (
            <button
              onClick={handleResetZoom}
              title="Reset Zoom (100%)"
              style={{
                background: "#ffbc3b",
                border: "none",
                color: "#1a1a37",
                fontSize: "11px",
                fontWeight: "700",
                padding: "2px 8px",
                borderRadius: "12px",
                cursor: "pointer",
                marginLeft: "4px",
              }}
            >
              Reset
            </button>
          )}
        </div>

        {/* Right: Close Button */}
        <button
          onClick={onClose}
          aria-label="Close viewer"
          title="Close (Esc)"
          style={{
            background: "rgba(255, 255, 255, 0.15)",
            border: "none",
            color: "#ffffff",
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            cursor: "pointer",
            fontSize: "18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#ffbc3b";
            e.currentTarget.style.color = "#1a1a37";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.15)";
            e.currentTarget.style.color = "#ffffff";
          }}
        >
          ✕
        </button>
      </div>

      {/* Main Image Stage */}
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "1150px",
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          minHeight: 0,
        }}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Previous Button (only visible when not zoomed in, or at 1x) */}
        {totalImages > 1 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handlePrev();
            }}
            aria-label="Previous photo"
            style={{
              position: "absolute",
              left: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "rgba(255, 255, 255, 0.2)",
              border: "none",
              color: "#ffffff",
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              cursor: "pointer",
              fontSize: "28px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 100001,
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#ffbc3b";
              e.currentTarget.style.color = "#1a1a37";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                "rgba(255, 255, 255, 0.2)";
              e.currentTarget.style.color = "#ffffff";
            }}
          >
            ‹
          </button>
        )}

        {/* Scalable & Draggable Image */}
        <img
          src={currentImage.url}
          alt={currentImage.description || currentImage.title || `Photo ${currentIndex + 1}`}
          onClick={handleToggleZoom}
          onMouseDown={handleMouseDown}
          draggable={false}
          title={scale > 1 ? "Drag to pan, click to zoom out" : "Click to zoom in (+)"}
          style={{
            maxWidth: "92%",
            maxHeight: "78vh",
            objectFit: "contain",
            borderRadius: "4px",
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.7)",
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
            transformOrigin: "center center",
            transition: isDragging ? "none" : "transform 0.2s cubic-bezier(0.2, 0, 0, 1)",
            cursor: scale > 1 ? (isDragging ? "grabbing" : "grab") : "zoom-in",
            pointerEvents: "auto",
          }}
        />

        {/* Next Button */}
        {totalImages > 1 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            aria-label="Next photo"
            style={{
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "rgba(255, 255, 255, 0.2)",
              border: "none",
              color: "#ffffff",
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              cursor: "pointer",
              fontSize: "28px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 100001,
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#ffbc3b";
              e.currentTarget.style.color = "#1a1a37";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                "rgba(255, 255, 255, 0.2)";
              e.currentTarget.style.color = "#ffffff";
            }}
          >
            ›
          </button>
        )}
      </div>

      {/* Bottom Controls / Filmstrip */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "8px",
          width: "100%",
          zIndex: 100002,
        }}
      >
        <span
          style={{
            color: "rgba(255, 255, 255, 0.6)",
            fontSize: "12px",
            letterSpacing: "0.5px",
          }}
        >
          {scale > 1
            ? "Drag image to inspect text • Click to reset zoom"
            : "Click photo or scroll wheel to zoom in to read text"}
        </span>

        {/* Bottom Thumbnail Strip (if multiple photos) */}
        {totalImages > 1 && (
          <div
            style={{
              display: "flex",
              gap: "10px",
              overflowX: "auto",
              maxWidth: "100%",
              padding: "4px 12px",
            }}
          >
            {images.map((img, idx) => (
              <img
                key={idx}
                src={img.url}
                alt={`Thumbnail ${idx + 1}`}
                onClick={() => {
                  setScale(1);
                  setPan({ x: 0, y: 0 });
                  onNavigate(idx);
                }}
                style={{
                  width: "52px",
                  height: "52px",
                  objectFit: "cover",
                  borderRadius: "4px",
                  cursor: "pointer",
                  opacity: idx === currentIndex ? 1 : 0.45,
                  border:
                    idx === currentIndex
                      ? "2px solid #ffbc3b"
                      : "2px solid transparent",
                  transform: idx === currentIndex ? "scale(1.08)" : "scale(1)",
                  transition: "all 0.2s ease",
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
